/****************************************************
 * Module Name:  TenderStats.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/

import { Box, Tooltip, Typography, ThemeProvider, createTheme, Stack, TextField } from "@mui/material";
import React, { useState, useEffect } from "react";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import Select from "react-select";
import { LoadingButton } from "@mui/lab";
import Constants from "../../../utility/constants";
import TenderLotProxy from "../../../utility/proxy/manager/tender/tenderLotProxy";
import { BiSearchAlt2 } from "react-icons/bi";
import TabMenuSnackbar from "../../common/tabMenuSnackbar";
import renderCellExpand from "../../common/dataGridCellExpand";
import Utility from "../../../utility/utility";
import NoPermission from "../../common/noPermission";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import PageTitle from "../../common/pageTitle";


const TenderStats = () => {
    const [companyTenderOptions, setCompanyTenderOptions] = useState([]);
    const [companyTender, setCompanyTender] = useState("");
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [loader, setLoader] = useState(false);
    const [middleLoader, setMiddleLoader] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [outerLoader, setOuterLoader] = useState();
    const [outerRows, setOuterRows] = useState([]);
    const [filterValues, setFilterValues] = useState({});
    const [fromDate, setFromDate] = useState(dayjs().subtract(2, 'month').startOf('month').toDate());
    const [toDate, setToDate] = useState(dayjs().toDate());
    const [CompanyDataLoader, setCompanyDataLoader] = useState(true);

    const local_Permission = Constants.USER_PERMISSION.filter(
        (iPermission) => iPermission.permissionid === Constants.PERMISSIONS.TENDER_OPERATION_SALES_BID);

    useEffect(() => {
        PageTitle("TENDER_REPORT_STATS");
    }, []);

    useEffect(() => {
        if (fromDate && toDate)
            getCompanyTender();
    }, [fromDate, toDate]);

    const getCompanyTender = () => {

        let reqData = {
            from: (fromDate !== null && fromDate !== "") ?
                dayjs(fromDate).format('YYYY-MM-DD') + "T00:00:00.000Z" : null,
            to: (toDate !== null && toDate !== "") ?
                dayjs(toDate).format('YYYY-MM-DD') + "T23:59:59.999Z" : null,
        }
        setOuterRows([]);
        setCompanyDataLoader(true);
        setCompanyTenderOptions([]);
        setCompanyTender("");
        let optionsArray = [];
        TenderLotProxy.GetCompanyTendersForReport(reqData, (response) => {
            response.map((data) => {
                let option = { value: data.tenderid, label: data.companyname + " | " + (data.tendername === null ? " - " : data.tendername), companyid: data.companyid };
                optionsArray.push(option);
                return true;
            });
            setCompanyDataLoader(false);
        })
        setCompanyTenderOptions(optionsArray);
    }

    const handleSearch = () => {
        setLoader(true);

        // need to fetch such companies and tenders which are not yet locked 
        // so both stone wise bid and lot wise bid status should be "IN_PROGRESS"

        TenderLotProxy.GetReportData(companyTender.value, companyTender.companyid, (response) => {
            if (response && Array.isArray(response)) {
                let transformedData = [];
                transformedData = response.map(item => ({
                    ...item,
                    lotwiseremaining: item.lotwiseremaining === 1 ? "COMPLETED" : "PENDING",
                }));
                const uniqueFilterValues = {
                    tendername: Utility.getDistinctOptions(transformedData, "tendername"),
                    minename: Utility.getDistinctOptions(transformedData, "minename"),
                    lotwiseremaining: Utility.getDistinctOptions(transformedData, "lotwiseremaining"),
                };
                setFilterValues(uniqueFilterValues);
                setLoader(false);
                setOuterLoader(false);
                setOuterRows(transformedData);
            } else {
                setLoader(false);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
            }
        });
    };

    const getOuterColumns = () => [
        {
            field: 'tenderlotid',
            headerName: 'ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => params.value,
            width: 80
        },
        {
            field: "lotcode",
            headerName: "LOT CODE",
            headerAlign: "center",
            align: "center",
            width: 120,
            filterOperators: Utility.defaultFilters(),
            renderCell: renderCellExpand,
            sortComparator: (v1, v2) => Utility.sortAlphaNumeric(v1, v2)
        },
        {
            field: 'tenderid',
            headerName: 'TENDER ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => params.value,
            width: 80
        },
        {
            field: 'tendername',
            headerName: 'TENDER NAME',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.tendername : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 140
        },
        {
            field: 'basicamount',
            headerName: 'BASIC AMOUNT',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value ? (params.value).toFixed(2) : params.value,
            renderCell: (params) => params.value ? (params.value).toFixed(2) : params.value,
            width: 140
        },
        {
            field: 'basicprice',
            headerName: 'BASIC PRICE',
            headerAlign: 'center',
            align: 'center',
            type: "number",
            valueFormatter: (params) => params.value ? (params.value).toFixed(2) : params.value,
            renderCell: (params) => params.value ? (params.value).toFixed(2) : params.value,
            width: 180
        },
        {
            field: 'carats',
            headerName: 'CARATS',
            headerAlign: 'center',
            align: 'center',
            type: "number",
            valueFormatter: (params) => (params.value).toFixed(2),
            renderCell: (params) => (params.value).toFixed(2),
            width: 180
        },
        {
            field: 'description',
            headerName: 'DESCRIPTION',
            headerAlign: 'center',
            align: 'center',
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'mineid',
            headerName: 'MINE ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => params.value,
            width: 80
        },
        {
            field: 'minename',
            headerName: 'MINE NAME',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.minename : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'pcs',
            headerName: 'PCS',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => params.value,
            width: 65
        },
        {
            field: "evaluate",
            headerName: "EVALUATE",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "boolean",
        },
        {
            field: 'size',
            headerName: 'SIZE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => params.value,
            width: 180
        },
        {
            field: 'totalstones',
            headerName: 'TOTAL STONES',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => params.value,
            width: 110
        },
        {
            field: 'planremaining',
            headerName: 'PLAN REMAINING',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => params.value,
            width: 170
        },
        {
            field: 'salesbidremaining',
            headerName: 'SALES BID REMAINING',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => params.value,
            width: 170
        },
        {
            field: 'manufbidremaining',
            headerName: 'MF BID REMAINING',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => params.value,
            width: 140
        },
        {
            field: 'stonewiseremaining',
            headerName: 'STONE WISE REMAINING',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => params.value,
            width: 170
        },
        {
            field: 'lotwiseremaining',
            headerName: 'LOT WISE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.lotwiseremaining : [],
            filterOperators: Utility.combinedFilters(),
            cellClassName: (params) => {
                return params.value === 'COMPLETED' ? 'greenCell boldText' : 'redCell boldText';
            },
            width: 170
        },
    ];

    let middleColumns = [
        {
            field: 'id',
            headerName: 'TENDER STONE ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => params.value,
            width: 160
        },
        {
            field: "totalplans",
            headerName: "PLAN COUNT",
            headerAlign: "center",
            align: "center",
            width: 150,
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => params.value,
        },
        {
            field: "remark",
            headerName: "REMARK",
            headerAlign: "center",
            align: "center",
            width: 150,
            renderCell: renderCellExpand,
        },
        {
            field: 'salependingplans',
            headerName: 'SALES PENDING',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => params.value,
            width: 120
        },
        {
            field: 'salesaprovedplans',
            headerName: 'SALES APPROVED',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => params.value,
            width: 140
        },
        {
            field: 'mfgpendingplans',
            headerName: 'MF PENDING',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => params.value,
            width: 120
        },
        {
            field: 'mfgapprovedplans',
            headerName: 'MF APPROVED',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => params.value,
            width: 120
        }
    ];


    const getDetailPanelHeight = React.useCallback(() => 'auto', []);

    const getDetailPanelContent = React.useCallback((row, middleColumns1, mainData) => <DetailPanelContent row={row} middleColumns1={middleColumns1} mainData={mainData} />,
        [],);

    function DetailPanelContent({ row: rowProp, middleColumns1, mainData }) {
        setMiddleLoader(true);

        let middlerows = [];
        const foundItem = mainData.find((idata) => {
            return rowProp.tenderlotid === idata.tenderlotid;
        });

        if (foundItem && foundItem.tenderstonelist) {
            middlerows = foundItem.tenderstonelist;
        }

        setMiddleLoader(false);
        return (
            <Stack
                sx={{ py: 2, height: '100%', boxSizing: 'border-box' }}
                direction="column"
            >
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "51vw",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "4px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .MuiTablePagination-toolbar, .MuiDataGrid-footerContainer": {
                            minHeight: "30px"
                        },
                        "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                            margin: 0
                        },
                        height: 'auto',
                        marginLeft: '16px',
                    }}
                >
                    <DataGridPro
                        disableRowSelectionOnClick={true}
                        density="compact"
                        columns={middleColumns1}
                        rows={middlerows}
                        getRowId={(row) => row.id}
                        loading={middleLoader}
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "2px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "15px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "22px",
                            },

                            fontSize: Constants.defaultTableFontSize, fontWeight: 'bold',
                            maxHeight: '30vh',
                        }}
                        // loading={isLoading}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    tenderlotid: false,
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                    />
                </Box>
            </Stack>

        );
    }

    const defaultMaterialTheme = createTheme();
    const outerColumns = getOuterColumns();

    return !Utility.isReadAllowed(local_Permission) ? <NoPermission /> : (
        <Box>

            {showSnackbar ? (
                <TabMenuSnackbar
                    show={true}
                    message={snackbarMessage}
                    variant={snackbarVariant}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={snackbarVariant === "success" ? "Success" :
                        snackbarVariant === "error" ? "Failed" : "Warning"}
                    handleClose={() => setShowSnackbar(false)}
                />
            ) : null}

            <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: "15px", marginTop: "15px" }}>
                {/* Company Tender */}
                <Box sx={{ display: "flex", justifyContent: "center", width: "35%", paddingRight: "0px", marginTop: "8px" }}>
                    <Typography sx={{ width: "21%", marginTop: "8px" }}>Company Tender</Typography>
                    <Select menuPlacement="auto"
                        blurInputOnSelect={false}
                        menuPortalTarget={document.body}
                        styles={{
                            ...Constants.Styles,
                            control: (base) => ({
                                ...base,
                                width: '27rem', // Adjust the width as needed
                                minHeight: "43px"
                            }),
                        }}
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        options={companyTenderOptions}
                        value={companyTender}
                        isClearable={true}
                        onChange={(e) => setCompanyTender(e)}
                    />
                    {CompanyDataLoader ?
                        (<Box sx={{ marginLeft: 2, marginTop: "8px" }}>
                            {<CircularProgress size={20} />}
                        </Box>) :
                        (<Box sx={{ marginLeft: 2, marginTop: "8px" }}>
                            <Tooltip title="Reload List">
                                <RefreshOutlinedIcon
                                    onClick={() => getCompanyTender()}
                                    color="primary"
                                    fontSize="medium"
                                    sx={{ cursor: "pointer" }}
                                />
                            </Tooltip>
                        </Box>)}
                </Box>

                <Box sx={{
                    display: "flex", width: "25%", gap: "15px"
                }}>
                    <Box
                        sx={{
                            display: "flex", flexDirection: "row",
                            "& .css-154xyx0-MuiInputBase-root-MuiOutlinedInput-root": {
                                height: "2.45rem",
                            },
                        }}
                        marginTop="8px"
                        gap="15px"
                        width="50%"
                    >
                        <Typography mt={1}>From</Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                className="picker"
                                renderInput={(props) => (
                                    <TextField
                                        fullWidth
                                        {...props}
                                        inputProps={{ ...props.inputProps, readOnly: true }}
                                    />
                                )}
                                value={fromDate}
                                inputFormat="DD/MM/YYYY"
                                onChange={(newValue) => {
                                    // Get the current time
                                    const now = dayjs();

                                    // Set startOfMonth to the first day of the selected month, but with the current system time
                                    const fromDate = dayjs(newValue)
                                        .hour(now.hour())
                                        .minute(now.minute())
                                        .second(now.second())
                                        .millisecond(now.millisecond())
                                        .toDate();

                                    setFromDate(fromDate);
                                }}
                            />
                        </LocalizationProvider>
                    </Box>
                    <Box
                        sx={{
                            display: "flex", flexDirection: "row",
                            "& .css-154xyx0-MuiInputBase-root-MuiOutlinedInput-root": {
                                height: "2.45rem",
                            },
                        }}
                        marginTop="8px"
                        gap="15px"
                        width="50%">
                        <Typography mt={1}>To</Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                className="picker"
                                renderInput={(props) => (
                                    <TextField
                                        fullWidth
                                        {...props}
                                        inputProps={{ ...props.inputProps, readOnly: true }}
                                    />
                                )}
                                value={toDate}
                                inputFormat="DD/MM/YYYY"
                                onChange={(newValue) => {
                                    // Get the current time
                                    const now = dayjs();

                                    // Set startOfMonth to the first day of the selected month, but with the current system time
                                    const toDate = dayjs(newValue)
                                        .hour(now.hour())
                                        .minute(now.minute())
                                        .second(now.second())
                                        .millisecond(now.millisecond())
                                        .toDate();

                                    setToDate(toDate);
                                }}
                            />
                        </LocalizationProvider>
                    </Box>
                </Box>

                {/* Submit */}
                <Box sx={{
                    width: "17%",
                    "& .Mui-disabled": {
                        backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                        color: "rgba(0, 0, 0, 0.26) !important"
                    },
                    marginTop: "8px", marginLeft: "10px"
                }}>
                    <LoadingButton
                        sx={{ mr: 1, mt: "0px" }}
                        variant="contained"
                        color="info"
                        onClick={handleSearch}
                        loading={loader}
                        loadingPosition="start"
                        startIcon={<BiSearchAlt2 />}
                        type="Submit"
                        disabled={!companyTender}
                    >
                        Search
                    </LoadingButton>
                </Box>
            </Box>

            <ThemeProvider theme={defaultMaterialTheme}>
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "100%",
                        position: "relative",
                        paddingTop: "20px",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .lavenderCell": {
                            color: "#080808",
                            backgroundColor: "#D8BFD8",
                        },
                        height: "71vh",
                    }}
                >
                    <DataGridPro
                        rowHeight={65}
                        disableRowSelectionOnClick={true}
                        density="compact"
                        columns={outerColumns}
                        rows={outerRows}
                        loading={outerLoader}
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "4px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "12px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "18px",
                            },

                            "& .redCell": {
                                color: "#080808",
                                backgroundColor: "#f5d7d7",
                            },
                            "& .greenCell": {
                                color: "#080808",
                                backgroundColor: "#B6D7A8"
                            },
                            "& .boldText ": {
                                fontWeight: "bold",
                            },
                            fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                        }}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        getRowId={(row) => row.tenderlotid}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    id: false,
                                    createdby: false,
                                    createdbyusername: false,
                                    lockedby: false,
                                    status: false,
                                    rapnetdate: false,
                                    updatedat: false,
                                    createdat: false,
                                    tenderid: false,
                                    mineid: false,
                                    description: false,
                                    size: false,
                                    basicprice: false,
                                    tenderlotid: false,
                                    tendername: false
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                        getDetailPanelHeight={getDetailPanelHeight}
                        getDetailPanelContent={({ row }) => {
                            if (!row.tenderstonelist || row.tenderstonelist.length === 0 || row.lotwiseremaining === 'COMPLETED') {
                                return false;
                            } else {
                                return getDetailPanelContent(row, middleColumns, outerRows)
                            }
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            top: "33px",
                            right: "265px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            "& .MuiButtonBase-root": {
                                minHeight: "30px",
                            },
                            "& .Mui-disabled": {
                                backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                                color: "rgba(0, 0, 0, 0.26) !important"
                            }
                        }}
                    >
                        {outerLoader ? (
                            <CircularProgress size="1rem" />
                        ) : (
                            <Tooltip title="Reload List">
                                <RefreshOutlinedIcon
                                    onClick={() => handleSearch()}
                                    color="primary"
                                    fontSize="medium"
                                    sx={{ cursor: "pointer" }}
                                />
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            </ThemeProvider>


        </Box>
    )
}
export default TenderStats;